import { CMSSection } from '@/cms/CMSSection';
import { SectionHeader } from '@/components/SectionHeader';
import { Typography } from '@/components/Typography';
import { Visibility } from '@/components/Visibility';
import type { ContentfulPlaceholderFragment } from '@/graphql/contentful/api';
import { useIsMdDown } from '@/hooks/useMedia';
import { useToggle } from '@/hooks/useToggle';
import type { PlaceholderType } from '@/integrations/contentful';

import type { WarrantyData } from './components';
import { ExploreSection, WarrantyDetails } from './components';

import * as S from './Warranty.styles';

export type WarrantyOpts = WarrantyData & {
  type: PlaceholderType;
  withSectionMargin?: boolean;
};

type WarrantyProps = WarrantyOpts & ContentfulPlaceholderFragment;

export const Warranty = ({
  heading,
  movementManufacture,
  subtext,
  type,
  warrantyDuration,
  warrantyExtDuration,
  warrantyTakingCareOfYourWatch,
  warrantyText,
  withSectionMargin,
}: WarrantyProps) => {
  const [isOpen, toggle] = useToggle();
  const isMobileView = useIsMdDown();
  const withMargin = withSectionMargin ?? !isMobileView;
  return (
    <CMSSection
      data-test-id={`WarrantySection ${type}`}
      withMargin={withMargin}
    >
      <S.Wrapper>
        <Visibility mdHidden>
          <S.StyledAccordion
            data-test-id='WarrantyAccordion'
            isOpen={isOpen}
            onClick={toggle}
            title={
              <Typography fontWeight='bold' textTransform='uppercase'>
                {heading ?? ''}
              </Typography>
            }
          >
            <S.SectionsWrapper>
              <WarrantyDetails
                data-test-id='WarrantyDetails'
                movementManufacture={movementManufacture}
                subtext={subtext}
                warrantyDuration={warrantyDuration}
                warrantyExtDuration={warrantyExtDuration}
                warrantyText={warrantyText}
              />
              <ExploreSection
                data-test-id='ExploreSection'
                warrantyTakingCareOfYourWatch={warrantyTakingCareOfYourWatch}
              />
            </S.SectionsWrapper>
          </S.StyledAccordion>
        </Visibility>

        <Visibility mdVisible>
          <SectionHeader data-testid='WarrantyHeader' text={heading ?? ''} />
          <S.SectionsWrapper>
            <WarrantyDetails
              data-test-id='WarrantyDetails'
              movementManufacture={movementManufacture}
              subtext={subtext}
              warrantyDuration={warrantyDuration}
              warrantyExtDuration={warrantyExtDuration}
              warrantyText={warrantyText}
            />
            <ExploreSection
              data-test-id='ExploreSection'
              warrantyTakingCareOfYourWatch={warrantyTakingCareOfYourWatch}
            />
          </S.SectionsWrapper>
        </Visibility>
      </S.Wrapper>
    </CMSSection>
  );
};

Warranty.displayName = 'Warranty';
